import { useState, useMemo } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Rating,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import dayjs from 'dayjs';
import { fetchTutorProfile, addTutorReview, updateTutorReview, deleteTutorReview } from '../../api/tutorProfile';
import ConfirmDialog from '../feedback/ConfirmDialog';
import ReviewSortControls from '../reviews/ReviewSortControls';
import sortReviewsFn from '../reviews/sortReviews';

const B = { primary: '#4357AD', dark: '#1E2A4A' };
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 3,
    '& fieldset': { borderColor: '#EDE9FE' },
    '&:hover fieldset': { borderColor: B.primary },
    '&.Mui-focused fieldset': { borderColor: B.primary },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: B.primary },
};

const emptyForm = { studentName: '', rating: 5, text: '', createdAt: null };


const TutorReviewsDialog = ({ open, onClose, tutor }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [editingReviewId, setEditingReviewId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [sortBy, setSortBy] = useState('newest');

  const tutorId = tutor?.id;

  const { data: profile, isLoading } = useQuery({
    queryKey: ['tutor-profile', tutorId],
    queryFn: () => fetchTutorProfile(tutorId),
    enabled: open && Boolean(tutorId),
  });

  const addMutation = useMutation({
    mutationFn: (payload) => addTutorReview(tutorId, payload),
    onSuccess: () => {
      enqueueSnackbar('Відгук додано', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['tutor-profile', tutorId] });
      setForm(emptyForm);
    },
    onError: () => enqueueSnackbar('Не вдалося додати відгук', { variant: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ reviewId, payload }) => updateTutorReview(reviewId, payload),
    onSuccess: () => {
      enqueueSnackbar('Відгук оновлено', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['tutor-profile', tutorId] });
      setForm(emptyForm);
      setEditingReviewId(null);
    },
    onError: () => enqueueSnackbar('Не вдалося оновити відгук', { variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (reviewId) => deleteTutorReview(reviewId),
    onSuccess: () => {
      enqueueSnackbar('Відгук видалено', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['tutor-profile', tutorId] });
    },
    onError: () => enqueueSnackbar('Не вдалося видалити відгук', { variant: 'error' }),
  });

  const handleSubmitReview = () => {
    if (!form.studentName.trim() || !form.text.trim()) {
      enqueueSnackbar('Заповніть усі поля', { variant: 'warning' });
      return;
    }
    const payload = {
      studentName: form.studentName,
      rating: form.rating,
      text: form.text,
      createdAt: form.createdAt ? dayjs(form.createdAt).format('YYYY-MM-DDTHH:mm:ss') : null,
    };
    if (editingReviewId) {
      updateMutation.mutate({ reviewId: editingReviewId, payload });
    } else {
      addMutation.mutate(payload);
    }
  };

  const handleEditReview = (review) => {
    setForm({
      studentName: review.studentName,
      rating: review.rating,
      text: review.text,
      createdAt: review.createdAt ? dayjs(review.createdAt) : null,
    });
    setEditingReviewId(review.id);
  };

  const handleCancelEdit = () => {
    setForm(emptyForm);
    setEditingReviewId(null);
  };

  const reviews = profile?.reviews || [];
  const sortedReviews = useMemo(() => sortReviewsFn(reviews, sortBy), [reviews, sortBy]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, boxShadow: '0 20px 60px rgba(67,87,173,0.18)', overflow: 'hidden' } }}
      >
        {/* Header */}
        <Box sx={{ background: `linear-gradient(135deg, ${B.dark} 0%, ${B.primary} 100%)`, px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <StarIcon sx={{ color: '#FEF3C7', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: '#fff' }}>Відгуки</Typography>
            {tutor?.name && <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>{tutor.name}</Typography>}
          </Box>
        </Box>

        <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: B.primary }} />
            </Box>
          ) : (
            <>
              {/* Sort controls */}
              {reviews.length > 1 && (
                <Box sx={{ mb: 2 }}>
                  <ReviewSortControls value={sortBy} onChange={setSortBy} />
                </Box>
              )}

              {/* Reviews list */}
              {reviews.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#8891A5' }}>Відгуків поки немає</Typography>
                </Box>
              ) : (
                <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                  {sortedReviews.map((review) => (
                    <Box
                      key={review.id}
                      sx={{ px: 2, py: 1.5, borderRadius: 3, bgcolor: '#F8F7FF', border: '1px solid #EDE9FE' }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar sx={{ width: 28, height: 28, bgcolor: `${B.primary}15`, color: B.primary, fontSize: 12, fontWeight: 700 }}>
                            {review.studentName?.charAt(0)?.toUpperCase()}
                          </Avatar>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ color: B.dark }}>
                            {review.studentName}
                          </Typography>
                          <Rating value={review.rating} readOnly size="small" />
                        </Stack>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Редагувати">
                            <IconButton size="small" onClick={() => handleEditReview(review)} sx={{ color: B.primary, '&:hover': { bgcolor: '#EDE9FE' } }}>
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Видалити">
                            <IconButton size="small" onClick={() => setConfirmDelete(review)} sx={{ color: '#EF4444', '&:hover': { bgcolor: '#FEE2E2' } }}>
                              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                      <Typography variant="body2" sx={{ mt: 0.5, color: '#475569', lineHeight: 1.5 }}>
                        {review.text}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#8891A5' }}>
                          {dayjs(review.createdAt).format('DD.MM.YYYY')}
                        </Typography>
                        {review.parentName && (
                          <Typography variant="caption" sx={{ color: B.primary, fontWeight: 600 }}>
                            від: {review.parentName}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}

              {/* Add/Edit review */}
              <Divider sx={{ borderColor: '#EDE9FE', mb: 2 }} />
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: B.dark }}>
                  {editingReviewId ? 'Редагувати відгук' : 'Додати відгук'}
                </Typography>
                {editingReviewId && (
                  <Button size="small" onClick={handleCancelEdit} sx={{ textTransform: 'none', color: '#8891A5', fontSize: 12 }}>
                    Скасувати
                  </Button>
                )}
              </Stack>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Ім'я учня"
                    size="small"
                    fullWidth
                    value={form.studentName}
                    onChange={(e) => setForm((prev) => ({ ...prev, studentName: e.target.value }))}
                    sx={fieldSx}
                  />
                  <DatePicker
                    label="Дата відгуку"
                    format="DD.MM.YYYY"
                    value={form.createdAt ? dayjs(form.createdAt) : null}
                    onChange={(val) => setForm((prev) => ({ ...prev, createdAt: val }))}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { ...fieldSx, minWidth: 160 },
                        placeholder: 'Сьогодні',
                      },
                    }}
                  />
                </Stack>
                <Box>
                  <Typography variant="caption" fontWeight={600} sx={{ color: '#8891A5', mb: 0.5, display: 'block' }}>Оцінка</Typography>
                  <Rating
                    value={form.rating}
                    onChange={(_, newValue) => setForm((prev) => ({ ...prev, rating: newValue || 1 }))}
                  />
                </Box>
                <TextField
                  label="Текст відгуку"
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  value={form.text}
                  onChange={(e) => setForm((prev) => ({ ...prev, text: e.target.value }))}
                  sx={fieldSx}
                />
                <Button
                  variant="contained"
                  onClick={handleSubmitReview}
                  disabled={addMutation.isPending || updateMutation.isPending}
                  sx={{
                    alignSelf: 'flex-end',
                    textTransform: 'none',
                    bgcolor: B.primary,
                    '&:hover': { bgcolor: '#3648A0' },
                    borderRadius: 3,
                    fontWeight: 700,
                    px: 3,
                    boxShadow: '0 4px 14px rgba(67,87,173,0.3)',
                  }}
                >
                  {editingReviewId ? 'Зберегти' : 'Додати'}
                </Button>
              </Stack>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none', color: '#8891A5', fontWeight: 600, borderRadius: 3, px: 2.5 }}>
            Закрити
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Видалити відгук?"
        description="Цю дію не можна скасувати."
        onCancel={() => setConfirmDelete(null)}
        onConfirm={async () => {
          await deleteMutation.mutateAsync(confirmDelete.id);
          setConfirmDelete(null);
        }}
      />
    </>
  );
};

export default TutorReviewsDialog;
